import Cookies from 'js-cookie'

// Storage type tracking - tracks which storage type is currently active
// This prevents cross-user token conflicts
const STORAGE_TYPE_KEY = 'authStorageType'; // 'localStorage' | 'sessionStorage' | null

function getActiveStorageType(expectedUserId = null) {
  // MULTI-USER FIX: If expectedUserId is provided, validate tokens belong to that user
  // This prevents returning storage type for a different user's tokens

  // CRITICAL: Check stored preference FIRST to prevent cross-user conflicts
  // This ensures we use the storage type that was set during the last login
  // ⚠️ FIX: Use localStorage instead of sessionStorage so preference is shared across tabs/windows
  const storedType = localStorage.getItem(STORAGE_TYPE_KEY);

  if (storedType === 'localStorage') {
    // Validate that localStorage actually has a token
    const token = localStorage.getItem('accessToken');
    if (token) {
      // MULTI-USER FIX: If expectedUserId provided, validate token belongs to that user
      if (expectedUserId) {
        const tokenUserId = getUserIdFromToken(token);
        if (tokenUserId === expectedUserId) {
          return 'localStorage';
        }
        // Token doesn't match expected user - check sessionStorage
      } else {
        return 'localStorage';
      }
    }
    // Stored type says localStorage but no token or wrong user - clear preference and check sessionStorage
    setActiveStorageType(null);
  } else if (storedType === 'sessionStorage') {
    // Validate that sessionStorage actually has a token
    const token = getAccessTokenFromSessionStorage(); // Uses temp_session_ prefix
    if (token) {
      // MULTI-USER FIX: If expectedUserId provided, validate token belongs to that user
      if (expectedUserId) {
        const tokenUserId = getUserIdFromToken(token);
        if (tokenUserId === expectedUserId) {
          return 'sessionStorage';
        }
        // Token doesn't match expected user - check localStorage
      } else {
        return 'sessionStorage';
      }
    }
    // Stored type says sessionStorage but no token or wrong user - clear preference and check localStorage
    setActiveStorageType(null);
  }

  // No stored preference or preference invalid - check which storage has tokens
  // This handles edge cases where preference was lost
  const localStorageToken = localStorage.getItem('accessToken');
  const sessionStorageToken = getAccessTokenFromSessionStorage();

  const hasLocalStorageToken = !!localStorageToken;
  const hasSessionStorageToken = !!sessionStorageToken;

  // MULTI-USER FIX: If expectedUserId provided, validate tokens belong to that user
  if (expectedUserId) {
    const localTokenUserId = localStorageToken ? getUserIdFromToken(localStorageToken) : null;
    const sessionTokenUserId = sessionStorageToken ? getUserIdFromToken(sessionStorageToken) : null;

    const localMatches = localTokenUserId === expectedUserId;
    const sessionMatches = sessionTokenUserId === expectedUserId;

    if (localMatches && sessionMatches) {
      // Both have tokens for this user - prefer localStorage
      setActiveStorageType('localStorage');
      return 'localStorage';
    } else if (localMatches) {
      setActiveStorageType('localStorage');
      return 'localStorage';
    } else if (sessionMatches) {
      setActiveStorageType('sessionStorage');
      return 'sessionStorage';
    } else {
      // No token for this user - return null
      return null;
    }
  }

  // No expectedUserId - original logic (for backward compatibility)
  // If both have tokens, this is a conflict - clear both and return null
  if (hasLocalStorageToken && hasSessionStorageToken) {
    clearCredentials();
    return null;
  }

  if (hasLocalStorageToken) {
    setActiveStorageType('localStorage'); // Set preference for future
    return 'localStorage';
  }
  if (hasSessionStorageToken) {
    setActiveStorageType('sessionStorage'); // Set preference for future
    return 'sessionStorage';
  }

  return null;
}

function setActiveStorageType(type) {
  // ⚠️ FIX: Use localStorage instead of sessionStorage so preference is shared across tabs/windows
  // This prevents users from being logged out when opening links in new tabs
  if (type === 'localStorage' || type === 'sessionStorage') {
    localStorage.setItem(STORAGE_TYPE_KEY, type);
  } else {
    localStorage.removeItem(STORAGE_TYPE_KEY);
  }
}

// Access token - localStorage (persistent)
function getAccessTokenFromLocalStorage() {
  return localStorage.getItem('accessToken') || null;
}

function setAccessTokenToLocalStorage(token) {
  // CRITICAL FIX FOR MULTI-USER: Only clear credentials if token is for a different user
  // This prevents clearing another user's tokens when they're logged in different windows
  const currentToken = getAccessTokenFromLocalStorage();

  if (currentToken && token) {
    // Extract userId from both tokens to compare
    const currentUserId = getUserIdFromToken(currentToken);
    const newUserId = getUserIdFromToken(token);

    if (currentUserId && newUserId && currentUserId !== newUserId) {
      // Different user - clear credentials to prevent conflicts
      clearCredentials();
    } else if (currentUserId && newUserId && currentUserId === newUserId) {
      // Same user - just update token, clear sessionStorage to prevent conflicts
      clearCredentialsFromSessionStorage();
    } else {
      // Can't determine user - clear to be safe
      clearCredentials();
    }
  } else {
    // No current token or no new token - safe to clear
    clearCredentials();
  }

  localStorage.setItem('accessToken', token);
  setActiveStorageType('localStorage');
}

function clearAccessTokenFromLocalStorage() {
  localStorage.removeItem('accessToken');
  // Clear storage type if this was the active one (check without userId to avoid recursion)
  const currentType = getActiveStorageType();
  if (currentType === 'localStorage') {
    setActiveStorageType(null);
  }
}

// Access token - sessionStorage (temporary)
// Access token - sessionStorage (temporary)
function getAccessTokenFromSessionStorage() {
  // Check if session is still active via cookie
  // If cookie is gone (browser closed), treat as logged out
  if (!Cookies.get('session_active')) {
    return null;
  }
  // Use localStorage with a special prefix so it's shared across tabs
  return localStorage.getItem('temp_session_accessToken') || null;
}

function setAccessTokenToSessionStorage(token) {
  // CRITICAL FIX FOR MULTI-USER: Only clear credentials if token is for a different user
  // This prevents clearing another user's tokens when they're logged in different windows
  const currentToken = getAccessTokenFromSessionStorage();

  if (currentToken && token) {
    // Extract userId from both tokens to compare
    const currentUserId = getUserIdFromToken(currentToken);
    const newUserId = getUserIdFromToken(token);

    if (currentUserId && newUserId && currentUserId !== newUserId) {
      // Different user - clear credentials to prevent conflicts
      clearCredentials();
    } else if (currentUserId && newUserId && currentUserId === newUserId) {
      // Same user - just update token, clear localStorage to prevent conflicts
      clearCredentialsFromLocalStorage();
    } else {
      // Can't determine user - clear to be safe
      clearCredentials();
    }
  } else {
    // No current token or no new token - safe to clear
    clearCredentials();
  }

  // Store in localStorage with special prefix so it's shared across tabs
  localStorage.setItem('temp_session_accessToken', token);

  // Set a session cookie (no expiration) to track the browser session
  // This cookie will be deleted when the browser closes, invalidating the localStorage token
  Cookies.set('session_active', 'true');

  setActiveStorageType('sessionStorage');
}

function clearAccessTokenFromSessionStorage() {
  localStorage.removeItem('temp_session_accessToken');
  // Only remove cookies if we are clearing credentials explicitly
  // We keep it if we are just updating tokens
  // But usually this is called from clearCredentials which implies logout
  Cookies.remove('session_active');

  // Clear storage type if this was the active one (check without userId to avoid recursion)
  const currentType = getActiveStorageType();
  if (currentType === 'sessionStorage') {
    setActiveStorageType(null);
  }
}

// Unified access token getter - ONLY checks the active storage type to prevent cross-user conflicts
// MULTI-USER FIX: Validates token belongs to expected user if provided
function getAccessToken(expectedUserId = null) {
  // MULTI-USER FIX: Get storage type with user validation
  const activeType = getActiveStorageType(expectedUserId);
  let token = null;

  if (activeType === 'localStorage') {
    token = getAccessTokenFromLocalStorage();
  } else if (activeType === 'sessionStorage') {
    token = getAccessTokenFromSessionStorage();
  } else {
    // Fallback: if no active type, check both (but this should rarely happen)
    // MULTI-USER FIX: If expectedUserId provided, validate tokens
    if (expectedUserId) {
      const localToken = getAccessTokenFromLocalStorage();
      const sessionToken = getAccessTokenFromSessionStorage();

      // Check which token belongs to expected user
      if (localToken) {
        const localUserId = getUserIdFromToken(localToken);
        if (localUserId === expectedUserId) {
          token = localToken;
        }
      }

      if (!token && sessionToken) {
        const sessionUserId = getUserIdFromToken(sessionToken);
        if (sessionUserId === expectedUserId) {
          token = sessionToken;
        }
      }
    } else {
      // No user validation - use original fallback
      token = getAccessTokenFromLocalStorage() || getAccessTokenFromSessionStorage();
    }
  }

  // Strict validation: reject "null", "undefined", or empty strings
  if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
    return null;
  }

  // MULTI-USER FIX: Final validation - ensure token belongs to expected user
  if (expectedUserId) {
    const tokenUserId = getUserIdFromToken(token);
    if (tokenUserId !== expectedUserId) {
      // Token doesn't belong to expected user - return null
      // This prevents using another user's token
      return null;
    }
  }

  return token;
}

// Refresh token - localStorage (persistent)
function getRefreshTokenFromLocalStorage() {
  return localStorage.getItem('refreshToken') || null;
}

function setRefreshTokenToLocalStorage(token) {
  // Note: clearCredentials is called in setAccessTokenToLocalStorage, so we don't need to call it again here
  // But we ensure consistency by checking if access token is also in localStorage
  localStorage.setItem('refreshToken', token);
  setActiveStorageType('localStorage');
}

function clearRefreshTokenFromLocalStorage() {
  localStorage.removeItem('refreshToken');
}

// Refresh token - sessionStorage (temporary)
function getRefreshTokenFromSessionStorage() {
  // Check if session is still active via cookie
  if (!Cookies.get('session_active')) {
    return null;
  }
  // Use localStorage with specific prefix
  return localStorage.getItem('temp_session_refreshToken') || null;
}

function setRefreshTokenToSessionStorage(token) {
  // Note: clearCredentials is called in setAccessTokenToSessionStorage
  // Store in localStorage with prefix
  localStorage.setItem('temp_session_refreshToken', token);
  // Ensure session cookie exists
  if (!Cookies.get('session_active')) {
    Cookies.set('session_active', 'true');
  }
  setActiveStorageType('sessionStorage');
}

function clearRefreshTokenFromSessionStorage() {
  localStorage.removeItem('temp_session_refreshToken');
  // Note: checkAccessTokenFromSessionStorage handles cookie removal
}

// Unified refresh token getter - ONLY checks the active storage type to prevent cross-user conflicts
function getRefreshToken() {
  const activeType = getActiveStorageType();
  let token = null;

  if (activeType === 'localStorage') {
    token = getRefreshTokenFromLocalStorage();
  } else if (activeType === 'sessionStorage') {
    token = getRefreshTokenFromSessionStorage();
  } else {
    // Fallback: if no active type, check both (but this should rarely happen)
    // This is only for backward compatibility during migration
    token = getRefreshTokenFromLocalStorage() || getRefreshTokenFromSessionStorage();
  }

  // Strict validation: reject "null", "undefined", or empty strings
  if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
    return null;
  }

  return token;
}

// Global - clear from localStorage
function clearCredentialsFromLocalStorage() {
  clearAccessTokenFromLocalStorage();
  clearRefreshTokenFromLocalStorage();
  clearTokenExpiration();
  setActiveStorageType(null);
}

// Global - clear from sessionStorage
function clearCredentialsFromSessionStorage() {
  clearAccessTokenFromSessionStorage();
  clearRefreshTokenFromSessionStorage();
  clearTokenExpiration();
  setActiveStorageType(null);
}

// Global - clear from both storage types (CRITICAL: Always use this before storing new credentials)
function clearCredentials() {
  clearAccessTokenFromLocalStorage();
  clearRefreshTokenFromLocalStorage();
  clearAccessTokenFromSessionStorage();
  clearRefreshTokenFromSessionStorage();
  clearTokenExpiration();

  // Also clean up any lingering temporary keys just in case
  localStorage.removeItem('temp_session_accessToken');
  localStorage.removeItem('temp_session_refreshToken');
  localStorage.removeItem('temp_session_tokenExpiresAt');

  setActiveStorageType(null);
}

// Cookies
function setAccessTokenToCookies(token) {
  Cookies.set('accessToken', token);
}

function setRefreshTokenToCookies(token) {
  Cookies.set('refresh_token', token);
}

function getAccessTokenToCookies() {
  // Backend sets 'access_token', but also check 'accessToken' for compatibility
  return Cookies.get('access_token') || Cookies.get('accessToken');
}

function getRefreshTokenToCookies() {
  return Cookies.get('refresh_token');
}

function clearCredentialsFromCookies() {
  // Clear both cookie name variations for compatibility
  Cookies.remove('access_token');
  Cookies.remove('accessToken');
  Cookies.remove('refresh_token');
  Cookies.remove('refreshToken');
  Cookies.remove('session_active'); // Also clear session flag
}

// Token expiration tracking
function setTokenExpiration(expiresIn) {
  // Validate expiresIn is a valid positive number
  if (!expiresIn || typeof expiresIn !== 'number' || expiresIn <= 0 || !Number.isFinite(expiresIn)) {
    expiresIn = 3600; // Default to 1 hour in seconds
  }

  // expiresIn is in seconds, convert to milliseconds and add to current time
  const expirationTimestamp = Date.now() + (expiresIn * 1000);

  // Store in the active storage type only
  const activeType = getActiveStorageType();
  if (activeType === 'localStorage') {
    localStorage.setItem('tokenExpiresAt', expirationTimestamp.toString());
    localStorage.removeItem('temp_session_tokenExpiresAt'); // Clear from other storage
  } else if (activeType === 'sessionStorage') {
    localStorage.setItem('temp_session_tokenExpiresAt', expirationTimestamp.toString());
    localStorage.removeItem('tokenExpiresAt'); // Clear from other storage
  } else {
    // Fallback: store in both if no active type (shouldn't happen in normal flow)
    localStorage.setItem('tokenExpiresAt', expirationTimestamp.toString());
    localStorage.setItem('temp_session_tokenExpiresAt', expirationTimestamp.toString());
  }
}

function getTokenExpiration() {
  const activeType = getActiveStorageType();

  if (activeType === 'localStorage') {
    const expiresAt = localStorage.getItem('tokenExpiresAt');
    return expiresAt ? Number.parseInt(expiresAt, 10) : null;
  } else if (activeType === 'sessionStorage') {
    // Check cookie validity
    if (!Cookies.get('session_active')) return null;
    const expiresAt = localStorage.getItem('temp_session_tokenExpiresAt');
    return expiresAt ? Number.parseInt(expiresAt, 10) : null;
  }

  // Fallback: check both
  const expiresAt = localStorage.getItem('tokenExpiresAt') || localStorage.getItem('temp_session_tokenExpiresAt');
  return expiresAt ? Number.parseInt(expiresAt, 10) : null;
}

function isTokenExpired() {
  const expiresAt = getTokenExpiration();
  if (!expiresAt) return true; // If no expiration stored, assume token might be expired and needs refresh
  // Add 30 second buffer to account for clock skew and network delays
  const buffer = 30 * 1000; // 30 seconds in milliseconds
  return Date.now() >= (expiresAt - buffer);
}

function isTokenExpiringSoon(bufferMinutes = 5) {
  const expiresAt = getTokenExpiration();
  if (!expiresAt) return false; // If no expiration stored, assume not expiring soon
  const bufferMs = bufferMinutes * 60 * 1000;
  return Date.now() >= (expiresAt - bufferMs);
}

function clearTokenExpiration() {
  localStorage.removeItem('tokenExpiresAt');
  localStorage.removeItem('temp_session_tokenExpiresAt');
  sessionStorage.removeItem('tokenExpiresAt'); // Legacy cleanup
}

// Note: clearCredentialsFromLocalStorageWithExpiration is kept for backward compatibility
// but is functionally identical to clearCredentialsFromLocalStorage
function clearCredentialsFromLocalStorageWithExpiration() {
  clearCredentialsFromLocalStorage();
}

// Helper function to extract tokens from response (handles different response structures)
// Helper function to extract tokens from response (handles different response structures)
function extractTokensFromResponse(response) {
  const data = response?.data || {};


  // Handle multiple response structures:
  // 1. response.data.accessToken (direct)
  // 2. response.data.data.accessToken (nested)
  // 3. response.data.access_token (snake_case)
  const accessToken = data.accessToken || data.data?.accessToken || data.access_token || data.data?.access_token || null;
  const refreshToken = data.refreshToken || data.data?.refreshToken || data.refresh_token || data.data?.refresh_token || null;


  // Extract expiresIn with fallback to default
  let expiresIn = data.expiresIn || data.data?.expiresIn || data.expires_in || data.data?.expires_in || 3600;

  // Validate expiresIn is a valid number
  if (typeof expiresIn !== 'number' || expiresIn <= 0 || !Number.isFinite(expiresIn)) {
    expiresIn = 3600; // Default to 1 hour in seconds
  }

  // Validate token format (basic JWT validation - should have 3 parts separated by dots)
  const isValidToken = (token) => {
    if (!token || typeof token !== 'string') return false;
    const parts = token.split('.');
    return parts.length === 3 && parts.every(part => part.length > 0);
  };

  return {
    accessToken: isValidToken(accessToken) ? accessToken : null,
    refreshToken: isValidToken(refreshToken) ? refreshToken : null,
    expiresIn: expiresIn
  };
}

// Auth-in-progress flag management (Safari fix for storage timing issues)
// This prevents router guard redirect loops when tokens are being written to storage
function setAuthInProgress() {
  // Use sessionStorage for this flag (cleared on tab close)
  sessionStorage.setItem('authInProgress', 'true');
  // Also set timestamp to auto-clear after 5 seconds (safety measure)
  sessionStorage.setItem('authInProgressTimestamp', Date.now().toString());
}

function clearAuthInProgress() {
  sessionStorage.removeItem('authInProgress');
  sessionStorage.removeItem('authInProgressTimestamp');
}

function isAuthInProgress() {
  const flag = sessionStorage.getItem('authInProgress');
  if (!flag) return false;

  // Check if flag is expired (older than 5 seconds - safety measure)
  const timestamp = sessionStorage.getItem('authInProgressTimestamp');
  if (timestamp) {
    const age = Date.now() - Number.parseInt(timestamp, 10);
    if (age > 5000) {
      // Auto-clear expired flag
      clearAuthInProgress();
      return false;
    }
  }

  return true;
}

// Helper function to check if using session storage (for backward compatibility)
function isUsingSessionStorage() {
  return getActiveStorageType() === 'sessionStorage';
}

// Helper function to decode JWT and extract userId (for validation)
function getUserIdFromToken(token) {
  if (!token) return null;

  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Decode payload (base64url)
    const payload = parts[1];
    // Replace URL-safe base64 characters
    const base64 = payload.replaceAll('-', '+').replaceAll('_', '/');
    // Add padding if needed
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);

    const decoded = JSON.parse(atob(padded));
    return decoded.userId || null;
  } catch (error) {
    return null;
  }
}

// Helper function to validate token belongs to expected user
function validateTokenUserId(token, expectedUserId) {
  if (!token || !expectedUserId) return false;

  const tokenUserId = getUserIdFromToken(token);
  return tokenUserId === expectedUserId;
}

export {
  getAccessTokenFromLocalStorage,
  setAccessTokenToLocalStorage,
  getAccessTokenFromSessionStorage,
  setAccessTokenToSessionStorage,
  clearAccessTokenFromSessionStorage,
  getAccessToken,
  getRefreshTokenFromLocalStorage,
  setRefreshTokenToLocalStorage,
  getRefreshTokenFromSessionStorage,
  setRefreshTokenToSessionStorage,
  clearRefreshTokenFromSessionStorage,
  getRefreshToken,
  clearRefreshTokenFromLocalStorage,
  clearAccessTokenFromLocalStorage,
  clearCredentialsFromLocalStorage,
  clearCredentialsFromSessionStorage,
  clearCredentials,
  setRefreshTokenToCookies,
  setAccessTokenToCookies,
  clearCredentialsFromCookies,
  getAccessTokenToCookies,
  getRefreshTokenToCookies,
  setTokenExpiration,
  getTokenExpiration,
  isTokenExpired,
  isTokenExpiringSoon,
  clearTokenExpiration,
  clearCredentialsFromLocalStorageWithExpiration,
  extractTokensFromResponse,
  setAuthInProgress,
  clearAuthInProgress,
  isAuthInProgress,
  getActiveStorageType,
  isUsingSessionStorage,
  getUserIdFromToken,
  validateTokenUserId
};
